import React, { useCallback, useContext, useState } from 'react';
import { submissionToUpdateGraphQLInput } from '../../SubmitGame/submission-helpers';
import { toast } from 'react-toastify';
import { SubmissionContext } from '../Context/SubmissionProvider';
import GameBaseFormName from '../GameBaseForm/GameBaseFormName';
import { getLatestBuild } from '../../../../../../common/build-utils';
import UploadGameFilesForm from '../GameBaseForm/UploadGameFilesForm';
import { updateSubmission } from '../Mutations/update-submission-mutation';
import ActionLink, { ACTION_LINK_HEIGHT, ActionLinkButton } from '../../../../Footer/ActionLink';
import { Box } from '@mui/material';
import { Submission } from '../submission-graphql';
import { GameJamLink, getGameJamLink } from '../gamejamHelper';
import SubmissionHeaderAlert from '../../../../../../common/Styleguide/Common/Alert';
import GameJamConfirmation from '../GameJamConfirmation';
import { ApolloError } from '@apollo/client';
import { DeleteState, SubmissionDeleteDialog } from './SubmissionOverview/SubmissionDeleteDialog';

interface BaseInfoStepProps {
  goToQA(submission: Submission, gameBuildId: string): void;
  goToDetails(): void;
}

// Dev land on this step only if the submission is in DRAFT status
// Next step is either:
// - QA if need for QA (or save & QA if modified)
// - Continue to next step if no need for QA (or save & continue to next step)
const BaseInfoStep: React.FC<BaseInfoStepProps> = ({ goToQA, goToDetails }) => {
  const {
    baseForm,
    baseFormProblems,
    submission,
    setAttemptedToSave,
    setIsModified,
    modified,
    detailsForm,
    updateSubmission: updateSubmissionInContext,
  } = useContext(SubmissionContext);

  if (!submission) {
    throw new Error('Submission is not defined');
  }

  const [isSaving, setIsSaving] = useState(false);
  const [showGameJamConfirmation, setShowGameJamConfirmation] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const [deleteState, setDeleteState] = React.useState<DeleteState>('not-started');
  const [apolloError, setApolloError] = React.useState<ApolloError | undefined>(undefined);

  const onDeleteClick = () => {
    if (deleteState !== 'not-started') {
      return;
    }
    setApolloError(undefined);
    setShowConfirmDialog(true);
  };

  const submit = useCallback(async () => {
    if (baseFormProblems && baseFormProblems.length > 0) {
      console.error(`Form problems: ${baseFormProblems}`);
      toast.error('Please check if all the mandatory fields marked with * are filled before continuing', { autoClose: 8000 });
      setAttemptedToSave(true);
      return;
    }

    setIsSaving(true);

    try {
      // If submission already have details filled (as category) we need to send along the details form to prevent overwriting with empty values
      const updateInput = submissionToUpdateGraphQLInput(
        submission!,
        'baseInfo',
        baseForm!,
        submission?.categoryId ? detailsForm : undefined,
      );
      const res = await updateSubmission(updateInput);
      const newSubmission = res.data!.developerSubmissionUpdate;
      updateSubmissionInContext(newSubmission);
      setIsModified(false);
      if (submission.gameJamId) {
        setShowGameJamConfirmation(true);
      }
    } catch (e) {
      console.error(e);
      const errorMsg = e instanceof Error ? e.message : 'UNKNOWN_ERROR';
      toast.error(errorMsg, { autoClose: 5000 });
    } finally {
      setIsSaving(false);
    }
  }, [baseFormProblems, setAttemptedToSave, submission, baseForm, detailsForm, updateSubmissionInContext, setIsModified]);

  const latestBuild = getLatestBuild(submission.gameBuilds);
  let actionButton: ActionLinkButton | null = null;

  if (baseForm.gameJamId) {
    if (modified) {
      actionButton = {
        title: 'Save changes',
        callback: () => {
          submit();
        },
      };
    }
  } else if (modified) {
    actionButton = { title: 'Save', callback: submit };
  } else if (!latestBuild.qaResult) {
    actionButton = {
      title: 'Go to QA',
      callback: () => {
        goToQA(submission, latestBuild.id);
      },
    };
  } else {
    actionButton = {
      title: 'Continue',
      callback: goToDetails,
    };
  }

  const link = getGameJamLink(submission.id);

  // when developers return to a gamejam submission they can update this,
  // but won't be able to proceed further, so show the same confirmation popup as for new submissions
  if (showGameJamConfirmation && submission.gameJamId) {
    return (
      <GameJamConfirmation
        submissionId={submission.id}
        gameJamId={submission.gameJamId}
        goToSubmission={() => {
          window.location.reload();
        }}
      />
    );
  }

  return (
    <Box sx={{ paddingBottom: `${ACTION_LINK_HEIGHT}px` }}>
      {submission.gameJamId && (
        <SubmissionHeaderAlert color="purple" title="Game Jam" key={'gamejam'}>
          You can preview your game here:
          <br />
          <GameJamLink href={link}>{link}</GameJamLink>
        </SubmissionHeaderAlert>
      )}
      <GameBaseFormName />
      <UploadGameFilesForm />
      {actionButton && (
        <ActionLink
          primaryButton={{
            title: actionButton.title,
            callback: actionButton.callback,
            isLoading: isSaving,
          }}
          primaryButtonDisabled={baseForm.uploadInProgress}
          middleButton={{
            title: 'Discard draft',
            color: 'red',
            callback: onDeleteClick,
          }}
          middleButtonDisabled={submission.status !== 'DRAFT'}
        />
      )}
      <SubmissionDeleteDialog
        id={submission.id}
        showConfirmDialog={showConfirmDialog}
        setShowConfirmDialog={setShowConfirmDialog}
        deleteState={deleteState}
        apolloError={apolloError}
        setApolloError={setApolloError}
        setDeleteState={setDeleteState}
      />
    </Box>
  );
};

export default BaseInfoStep;
